/**
 * The agent-proposed values the user is asked to confirm or correct.
 *
 * Counterpart to askUserQuestion, and the same contract: the backend's
 * VerifyWithUser tool has no side effects, it validates the items and echoes a
 * normalized copy in its tool result, and that result is what we render. The
 * verdicts go back as an ordinary user message, which is why the text restates
 * every label and original value — the strategy and calendar agents replay history
 * as text only and never see the tool call.
 */
import type { ChatToolEvent, StreamingToolEvent } from "@/types/chat.type";

export const VERIFY_WITH_USER_TOOL = "VerifyWithUser";

/**
 * Sent when the user declines to check the values. Like the questions card this
 * has to be a real message rather than a local dismiss — the transcript is the
 * only durable state — and it says what the skip *means*, so the agent carries on
 * knowing the values are still unconfirmed rather than treating silence as assent.
 */
export const SKIP_VERIFY_MESSAGE =
  "Skipping this check. Treat all of those values as unconfirmed and carry on.";

/** One value to check. An empty `value` means the agent searched and found none. */
export type VerifyItem = {
  label: string;
  value: string;
  note?: string;
};

export type VerifyRequest = {
  intro?: string;
  items: VerifyItem[];
};

/**
 * One row's verdict. A rejected row may carry a correction; rejected with no
 * correction is the honest "that's wrong and I don't have the right one" case.
 */
export type VerifyVerdict = {
  state: "unset" | "confirmed" | "rejected";
  correction: string;
};

export const emptyVerdict = (): VerifyVerdict => ({ state: "unset", correction: "" });

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseItem(raw: unknown): VerifyItem | null {
  if (!isRecord(raw)) return null;
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  // A row with nothing to name it is a broken call. A row with no value is not —
  // "I looked for their Facebook and found nothing" is worth checking too.
  if (!label) return null;
  const value = typeof raw.value === "string" ? raw.value.trim() : "";
  const note = typeof raw.note === "string" && raw.note.trim() ? raw.note.trim() : undefined;
  return note ? { label, value, note } : { label, value };
}

/**
 * The values a VerifyWithUser event wants checked, or null if it isn't one.
 *
 * Read strictly from the validated `items` the tool echoes back, never from the raw
 * input: a call the backend rejected (malformed, or a second card in one turn)
 * carries no items, and trusting its input would put a card on screen for values
 * the agent was told it never got to raise. An in-flight call has no result yet
 * either, which is why no card appears until the call lands.
 */
export function parseVerifyRequest(
  event: Pick<ChatToolEvent | StreamingToolEvent, "toolName" | "result">,
): VerifyRequest | null {
  if (event.toolName !== VERIFY_WITH_USER_TOOL) return null;
  if (!isRecord(event.result) || !Array.isArray(event.result.items)) return null;
  const items = event.result.items
    .map(parseItem)
    .filter((item): item is VerifyItem => item !== null);
  if (items.length === 0) return null;
  const intro =
    typeof event.result.intro === "string" && event.result.intro.trim()
      ? event.result.intro.trim()
      : undefined;
  return intro ? { intro, items } : { items };
}

/** True once every row has been ruled on — i.e. submitting is allowed. */
export function isVerifyComplete(
  items: VerifyItem[],
  verdicts: VerifyVerdict[],
): boolean {
  return items.every((_, i) => (verdicts[i]?.state ?? "unset") !== "unset");
}

/**
 * One row as a line the agent can act on without ambiguity. Both the verdict and
 * what it applies to are spelled out, so a corrected value can never be mistaken
 * for the original and "confirmed — there is none" cannot read as "unanswered".
 */
function formatVerifyLine(item: VerifyItem, verdict: VerifyVerdict | undefined): string {
  const state = verdict?.state ?? "unset";
  const correction = verdict?.correction.trim() ?? "";
  const found = item.value.trim();

  // Submitting is gated on every row being ruled on, so this is unreachable in
  // practice — but if it ever leaks out, the agent should hear the truth.
  if (state === "unset") return `${item.label}: not checked`;

  if (state === "confirmed") {
    return found
      ? `${item.label}: confirmed — ${found}`
      : `${item.label}: confirmed — there is none`;
  }
  if (correction) {
    return found
      ? `${item.label}: corrected — ${correction} (not ${found})`
      : `${item.label}: corrected — ${correction} (you found none)`;
  }
  return found
    ? `${item.label}: wrong — ${found} is not right, and I don't have the correct one`
    : `${item.label}: wrong — there is one, but I don't have it to hand`;
}

/** The user message carrying the verdicts. Self-contained, like the answers message. */
export function formatVerifyMessage(
  request: VerifyRequest,
  verdicts: VerifyVerdict[],
): string {
  const lines = request.items.map(
    (item, i) => `- ${formatVerifyLine(item, verdicts[i])}`,
  );
  return `Checked what you found:\n${lines.join("\n")}`;
}
